import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import CountryFlag from '../../components/CountryFlag';

import Button from '../../components/Button/Button';
import InputText from '../../components/Input/InputText';
import InputTextArea from '../../components/Input/InputTextArea';
import Select from '../../components/Select/Select';
import Typography from '../../components/Typography';
import countryCodes from '../../utils/CountryCodes';
import { getPlatform } from '../../utils/socialMediaUtils';
import { theme } from '../../theme';
import EditIcon from '../../components/Icons/EditIcon';
import CheckIcon from '../../components/Icons/CheckIcon';
import UploadFileIcon from '../../components/Icons/UploadFileIcon';
import DeleteOutlineIcon from '../../components/Icons/DeleteOutlineIcon';

const PersonalDetailsStep = ({ formData, setFormData, file, setFile }) => {
  const { t } = useTranslation('completeProfile');
  const [newLink, setNewLink] = useState('');
  const [errors] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({ url: '', title: '' });
  const editRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (!selected.type.startsWith('image/')) return;
    if (selected.size > 5 * 1024 * 1024) return;
    setFile(selected);
  };

  const triggerFileInput = () => {
    document.getElementById('file').click();
  };

  const extractTitleFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');
      const pathSegments = urlObj.pathname.split('/').filter(segment => segment);

      // For social media platforms, try to extract meaningful title
      if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
        return pathSegments[0] ? `@${pathSegments[0]}` : hostname;
      }
      if (hostname.includes('instagram.com')) {
        return pathSegments[0] ? `@${pathSegments[0]}` : hostname;
      }
      if (hostname.includes('linkedin.com')) {
        return pathSegments[1] ? pathSegments[1] : hostname;
      }
      if (hostname.includes('github.com')) {
        return pathSegments[0] ? pathSegments[0] : hostname;
      }

      // Default: use hostname
      return hostname;
    } catch {
      return url;
    }
  };

  const handleAddLink = () => {
    if (newLink.trim()) {
      const platform = getPlatform(newLink);
      const title = extractTitleFromUrl(newLink);

      const isDuplicate = formData.socialMediaLinks.some(
        link => link.url.toLowerCase() === newLink.toLowerCase()
      );

      if (isDuplicate) {
        toast.error(t('toasts.duplicateLink'));
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        socialMediaLinks: [
          ...prevState.socialMediaLinks,
          { url: newLink, platform, title }
        ]
      }));
      setNewLink('');
    }
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = formData.socialMediaLinks.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, socialMediaLinks: updatedLinks }));
  };

  const startEditing = (index, field, currentValue) => {
    setEditingIndex(index);
    setEditingField(field);
    setTempValues({ ...tempValues, [field]: currentValue });

    // Focus and position cursor at the end after state update
    setTimeout(() => {
      const input = document.querySelector('input[type="text"]:focus');
      if (input) {
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }, 0);
  };

  const saveEdit = () => {
    if (editingIndex !== null && editingField) {
      const updatedLinks = [...formData.socialMediaLinks];
      updatedLinks[editingIndex] = {
        ...updatedLinks[editingIndex],
        [editingField]: tempValues[editingField]
      };

      // If URL is being edited, update the platform and title
      if (editingField === 'url') {
        updatedLinks[editingIndex].platform = getPlatform(tempValues[editingField]);
        if (!tempValues.title) {
          updatedLinks[editingIndex].title = extractTitleFromUrl(tempValues[editingField]);
        }
      }

      setFormData(prev => ({ ...prev, socialMediaLinks: updatedLinks }));
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingField(null);
    setTempValues({ url: '', title: '' });
  };

  // Handle click outside to save edit
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (editRef.current && !editRef.current.contains(event.target) && editingIndex !== null) {
        saveEdit();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingIndex, editingField, tempValues]);

  const selectedCountry = countryCodes.find(code => code.dial_code === formData.phonePrefix);

  return (
    <>
      {/* Avatar Upload */}
      <UserAvatarRow>
        {file ? (
          <ImageWrapper onClick={triggerFileInput} style={{ cursor: 'pointer' }}>
            <AvatarImage src={URL.createObjectURL(file)} alt={t('form.userAvatar.altText')} />
          </ImageWrapper>
        ) : formData.userAvatar ? (
          <ImageWrapper onClick={triggerFileInput} style={{ cursor: 'pointer' }}>
            <AvatarImage src={formData.userAvatar} alt={t('form.userAvatar.altText')} />
          </ImageWrapper>
        ) : (
          <EmptyImageWrapper onClick={triggerFileInput} style={{ cursor: 'pointer' }}>
            <EmptyAvatarImage src={t('form.userAvatar.defaultIcon')} />
          </EmptyImageWrapper>
        )}
        <UploadField>
          <UploadAvatarButton>
            <UploadFileIcon fill={theme.colors.defaultStrong} width="20px" height="20px" />
            <Typography $variant="body-1-semibold" color={theme.colors.defaultStrong}>{t('form.userAvatar.uploadImage')}</Typography>
          </UploadAvatarButton>
          <InputFile type="file" id="file" onChange={handleFileChange} accept="image/*" />
        </UploadField>
        {errors.userAvatar && <ErrorMsg>{errors.userAvatar}</ErrorMsg>}
      </UserAvatarRow>

      {/* Name and Last Name */}
      <FormRow>
        <NameInputWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.name.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.required')})</Typography>
          </LabelGroup>
          <InputText
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            $size=""
            placeholder={t('form.name.placeholder')}
            $variant={errors.name ? 'error' : ''}
            required
          />
          {errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
        </NameInputWrapper>
        <LastNameInputWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.lastName.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.required')})</Typography>
          </LabelGroup>
          <InputText
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            $size=""
            placeholder={t('form.lastName.placeholder')}
            $variant={errors.lastName ? 'error' : ''}
            required
          />
          {errors.lastName && <ErrorMsg>{errors.lastName}</ErrorMsg>}
        </LastNameInputWrapper>
      </FormRow>



      {/* Phone */}
      <FormRow>
        <PhonePrefixInputWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.phonePrefix.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.optional')})</Typography>
          </LabelGroup>
          <PhonePrefixInput>
            {selectedCountry && (
              <PrefixFlag countryCode={selectedCountry.code} height="20" />
            )}
            <PrefixSelect
              name="phonePrefix"
              value={formData.phonePrefix}
              onChange={handleChange}
              $size=""
              $variant={errors.phonePrefix ? 'error' : ''}
              autoComplete="off"
            >
              {countryCodes.map((code) => (
                <option key={`${code.dial_code}-${code.code}`} value={code.dial_code}>
                  {code.name} ({code.dial_code})
                </option>
              ))}
            </PrefixSelect>
          </PhonePrefixInput>
          {errors.phonePrefix && <ErrorMsg>{errors.phonePrefix}</ErrorMsg>}
        </PhonePrefixInputWrapper>
        <PhoneInputWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.phoneNumber.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.optional')})</Typography>
          </LabelGroup>
          <InputText
            type="text"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            $size=""
            placeholder={t('form.phoneNumber.placeholder')}
            $variant={errors.phoneNumber ? 'error' : ''}
            autoComplete="off"
          />
          {errors.phoneNumber && <ErrorMsg>{errors.phoneNumber}</ErrorMsg>}
        </PhoneInputWrapper>
      </FormRow>

      {/* Biography */}
      <FormRow>
        <InputTextAreaWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.biography.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.optional')})</Typography>
          </LabelGroup>
          <InputTextArea
            style={{ fieldSizing: 'content' }}
            name="description"
            value={formData.description}
            onChange={handleChange}
            $size=""
            autoComplete="off"
          />
        </InputTextAreaWrapper>
      </FormRow>

      {/* Social Media Links */}
      <SocialMediaLinksRow>
        <InputLinksWrapper>
          <LabelGroup>
            <Typography $variant="body-2-medium">{t('form.socialMedia.label')}</Typography>
            <Typography $variant="body-2-medium" $textTransform="capitalize" color={theme.colors.defaultWeak}>({t('form.fields.optional')})</Typography>
          </LabelGroup>
          <LinksInputInnerWrapper>
            <InputText
              type="text"
              name="socialMediaLink"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              placeholder={t('form.socialMedia.linkPlaceholder')}
              $size=""
            />
            <Button $variant="outline" size="l" sizeTablet="l" sizeMobile="l" style={{ alignSelf: 'stretch' }} type="button" onClick={handleAddLink}>
              {t('form.socialMedia.addLink')}
            </Button>
          </LinksInputInnerWrapper>
        </InputLinksWrapper>
        {formData.socialMediaLinks.length > 0 && (
          <LinksList>
            {formData.socialMediaLinks.map((link, index) => {
              const isEditing = editingIndex === index;

              return (
                <SocialLinkItem key={index} ref={isEditing ? editRef : null}>
                  <LinkContentWrapper>
                    <LinkDetailsWrapper>
                      {/* URL Field */}
                      <LinkFieldWrapper>
                        {isEditing && editingField === 'url' ? (
                          <EditInputWrapper>
                            <LinkValue>
                              <LinkLabel as="p" $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('form.socialMedia.urlLabel')}</LinkLabel>
                              <EditInput
                                type="text"
                                value={tempValues.url}
                                onChange={(e) => setTempValues({ ...tempValues, url: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit();
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                              />
                            </LinkValue>
                            <Button
                              $variant="ghost"
                              $size="small"
                              $iconOnly
                              onClick={saveEdit}
                              title={t('form.socialMedia.saveChanges')}
                            >
                              <CheckIcon fill={theme.colors.successMain} width="16px" height="16px" />
                            </Button>
                          </EditInputWrapper>
                        ) : (
                          <LinkValueWrapper>
                            <LinkValue>
                              <LinkLabel as="p" $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('form.socialMedia.urlLabel')}</LinkLabel>
                              <LinkText as="p" $variant="body-1-regular" color={theme.colors.defaultWeak}>{link.url}</LinkText>
                            </LinkValue>
                            <Button
                              $variant="ghost"
                              $size="small"
                              $iconOnly
                              onClick={() => startEditing(index, 'url', link.url)}
                              title={t('form.socialMedia.editUrl')}
                            >
                              <EditIcon fill={theme.colors.defaultStrong} width="16px" height="16px" />
                            </Button>
                          </LinkValueWrapper>
                        )}
                      </LinkFieldWrapper>

                      {/* Title Field */}
                      <LinkFieldWrapper>
                        {isEditing && editingField === 'title' ? (
                          <EditInputWrapper>
                            <LinkValue>
                              <LinkLabel as="p" $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('form.socialMedia.titleLabel')}</LinkLabel>
                              <EditInput
                                type="text"
                                value={tempValues.title}
                                onChange={(e) => setTempValues({ ...tempValues, title: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit();
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                              />
                            </LinkValue>
                            <Button
                              $variant="ghost"
                              $size="small"
                              $iconOnly
                              onClick={saveEdit}
                              title={t('form.socialMedia.editTitle')}
                            >
                              <CheckIcon fill={theme.colors.successMain} width="16px" height="16px" />
                            </Button>
                          </EditInputWrapper>
                        ) : (
                          <LinkValueWrapper>
                            <LinkValue>
                              <LinkLabel as="p" $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('form.socialMedia.titleLabel')}</LinkLabel>
                              <LinkTitle as="p" $variant="body-1-regular" color={theme.colors.defaultWeak}>{link.title || extractTitleFromUrl(link.url)}</LinkTitle>
                            </LinkValue>
                            <Button
                              $variant="ghost"
                              $size="small"
                              $iconOnly
                              onClick={() => startEditing(index, 'title', link.title || extractTitleFromUrl(link.url))}
                              title={t('form.socialMedia.editTitle')}
                            >
                              <EditIcon fill={theme.colors.defaultStrong} width="16px" height="16px" />
                            </Button>
                          </LinkValueWrapper>
                        )}
                      </LinkFieldWrapper>
                    </LinkDetailsWrapper>
                  </LinkContentWrapper>

                  <DeleteLinkButton
                    $variant="ghost"
                    $size="small"
                    onClick={() => handleRemoveLink(index)}
                    // style={{ borderRadius: "0px", height: "auto" }}
                    $iconOnly
                  >
                    <DeleteOutlineIcon fill={theme.colors.defaultMain} width="16px" height="16px" />
                    {/* {t('form.socialMedia.removeLink')} */}
                  </DeleteLinkButton>
                </SocialLinkItem>
              )
            })}
          </LinksList>
        )}
      </SocialMediaLinksRow>
    </>
  );
};

PersonalDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  file: PropTypes.object,
  setFile: PropTypes.func.isRequired,
};

export default PersonalDetailsStep;

// Styled Components
const UserAvatarRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ImageWrapper = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
`;

const LinkValue = styled.div`
    display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  flex: 1;
  min-width: 0;
`;

const LinkLabel = styled(Typography)`
  min-width: 40px;
`;

const EmptyImageWrapper = styled(ImageWrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  background: ${({ theme }) => theme.fill.defaultSubtle};
`;

const EmptyAvatarImage = styled.img`
  width: 60px;
  height: 60px;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const InputWrapper = styled.label`
  display: flex;
  flex-direction: column; 
  gap: 6px;
  flex-grow: 1;
  width: 100%;
`;

const NameInputWrapper = styled(InputWrapper)``;
const LastNameInputWrapper = styled(InputWrapper)``;
const AddressInputWrapper = styled(InputWrapper)``;
const PhonePrefixInputWrapper = styled(InputWrapper)``;
const PhoneInputWrapper = styled(InputWrapper)``;
const InputTextAreaWrapper = styled(InputWrapper)``;
const InputLinksWrapper = styled(InputWrapper)``;

const PhonePrefixInput = styled.div`
  position: relative;
`;

const PrefixFlag = styled(CountryFlag)`
  position: absolute;
  top: 10px;
  left: 8px;
`;

const PrefixSelect = styled(Select)`
  padding-left: 36px;
`;

const LabelGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xxs};
  justify-content: flex-start;
  align-items: center;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;

const SocialMediaLinksRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const SocialLinkItem = styled.li`
  // display: flex;
  // flex-direction: row;
  // align-items: stretch;
  // border-radius: ${({ theme }) => theme.radius.xs};
  // border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  // width: 100%;
  // max-width: 100%;
  // min-width: 0;
  // justify-content: flex-start;
  // background: ${({ theme }) => theme.fill.defaultSubtle};
  // box-sizing: border-box;
  // overflow: hidden;

    display: flex;
  flex-direction: row;
  align-items: stretch;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  justify-content: flex-start;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  box-sizing: border-box;
  overflow: hidden;
`;

const DeleteLinkButton = styled(Button)`
  border-radius: 0px;
  height: auto;
  width: 40px;
`;

const LinkContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  border-right: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const LinkValueWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.sizing.xl};
  min-height: 34px;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: space-between;
  }
`;

const LinkText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  width: 100%;
  line-height: normal;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    // max-width: 300px;
    max-width: 100%;
  }

  // @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
  //   max-width: 200px;
  // }
`;

const LinkDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LinkFieldWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  height: ${({ theme }) => theme.sizing.xl};
  min-height: 34px;
  padding-left: ${({ theme }) => theme.sizing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

const LinkTitle = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  width: 100%;
  line-height: normal;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const EditInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${({ theme }) => theme.sizing.xs};
  height: ${({ theme }) => theme.sizing.xl};
`;

const EditInput = styled(InputText)`
  padding: 0px;
  border: none;
  background: transparent;
  width: 100%;
  height: ${({ theme }) => theme.sizing.xl};
  min-height: 34px;
  
  &:focus {
    outline: none;
    border: none;
    background: transparent;
  }
`;

const LinksInputInnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.xs};
  }
`;

const LinksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  width: 100%;
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;

const UploadAvatarButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  height: ${({ theme }) => theme.sizing.xl};
  transition: all 0.2s ease-in-out;
  gap: ${({ theme }) => theme.radius.xs};
  padding-left: ${({ theme }) => theme.sizing.xs};
  padding-right: ${({ theme }) => theme.sizing.xs};
  cursor: pointer;

  &:hover {
    background-color: ${theme.palette.alabaster[100]};
  }
`;

const UploadField = styled.label`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const InputFile = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden !important;
  position: absolute;
  z-index: -1;
`;
